// @flow
import Units, {
    UnitSystemsEnum,
    UnitDimensionsEnum,
    UnitSystemToConvertSystemMap
} from 'common/enums/UnitEnum';
import { DataUtils } from 'common/utils/DataUtils';
import StringUtils from 'common/utils/StringUtils';
import * as Immutable from 'immutable';
import { DimensionsMapping } from 'common/models/internal/DimensionsMapping';
import type { List } from 'immutable';
import type { UnitType } from 'common/enums/UnitEnum';
import type { VolumeLimitUnitEnum } from 'common/models/flow/VolumeLimit';

const unitMeasureToDimensionsMapping = {
    length: 'DISTANCE',
    mass: 'WEIGHT',
    time: 'TIME',
    speed: 'DISTANCE', // speed uses the mapping for distance
    temperature: 'TEMPERATURE',
    volume: 'VOLUME'
};

type ConvertToOptionsType = {
    value: number,
    conversionScale?: string,
    convertToUnit?: string
};

type ConvertValueOptionsType = {
    value: number | string,
    sourceUnit?: Object,
    unit?: string,
    preferredUnits?: Array<UnitType>,
    totalValue?: number | string,
    conversionScale?: string,
    roundOff?: boolean,
    padLeft?: boolean,
    decimals?: number,
    pluralizeUnit?: boolean,
    showUnitAbbr?: boolean
};

export default class UnitConversionHelper {
    dimensionsMapping: List<DimensionsMapping>;

    constructor(options: { dimensionsMapping?: ?List<DimensionsMapping> }) {
        this.dimensionsMapping = options.dimensionsMapping
            ? Immutable.List([
                  ...options.dimensionsMapping,
                  DimensionsMapping.fromJS({
                      dimension: UnitDimensionsEnum[Units.measures.time],
                      system: UnitSystemsEnum.Metric
                  })
              ])
            : this.getDefaultDimensionsMapping();
    }

    getDefaultDimensionsMapping = () => {
        return Immutable.List([
            DimensionsMapping.fromJS({
                dimension: UnitDimensionsEnum[Units.measures.length],
                system: UnitSystemsEnum.Metric
            }),
            DimensionsMapping.fromJS({
                dimension: UnitDimensionsEnum[Units.measures.mass],
                system: UnitSystemsEnum.Metric
            }),
            DimensionsMapping.fromJS({
                dimension: UnitDimensionsEnum[Units.measures.time],
                system: UnitSystemsEnum.Metric
            }),
            DimensionsMapping.fromJS({
                dimension: UnitDimensionsEnum[Units.measures.temperature],
                system: UnitSystemsEnum.Metric
            }),
            DimensionsMapping.fromJS({
                dimension: UnitDimensionsEnum[Units.measures.volume],
                system: UnitSystemsEnum.Metric
            })
        ]);
    };

    convertMetrePerSecond = (options: ConvertValueOptionsType) => {
        return this.convertValue({
            ...options,
            sourceUnit: Units.list.speed.MetrePerSecond
        });
    };

    convertDegreeCelsius = (options: ConvertValueOptionsType) => {
        return this.convertValue({
            ...options,
            sourceUnit: Units.list.temperature.DegreeCelsius
        });
    };

    convertMeters = (options: ConvertValueOptionsType) => {
        return this.convertValue({
            ...options,
            sourceUnit: Units.list.length.Meter
        });
    };

    convertMeterCube = (options: ConvertValueOptionsType) => {
        return this.convertValue({
            ...options,
            sourceUnit: Units.list.volume.CubicMeter
        });
    };

    convertVolume(
        value: number,
        sourceVolumeUnit: VolumeLimitUnitEnum,
        decimals: ?number = 4
    ) {
        if (sourceVolumeUnit === 'METERS_CUBIC') {
            return this.convertMeterCube({
                value,
                pluralizeUnit: false,
                roundOff: true,
                decimals
            });
        }
        return {
            totalValue: null,
            value,
            unit: null,
            valueString: value.toString()
        };
    }

    convertSeconds = (options: ConvertValueOptionsType) => {
        return this.convertValue({
            ...options,
            sourceUnit: Units.list.time.Second
        });
    };

    convertKilograms = (options: ConvertValueOptionsType) => {
        return this.convertValue({
            ...options,
            sourceUnit: Units.list.mass.Kilogram
        });
    };

    convertValue = (options: ConvertValueOptionsType) => {
        const {
            value,
            totalValue,
            sourceUnit,
            unit,
            preferredUnits,
            conversionScale,
            padLeft,
            roundOff = true,
            decimals = 2,
            pluralizeUnit,
            showUnitAbbr
        } = options;
        if (!sourceUnit || !sourceUnit.abbr) {
            // if no unit, or it's not from Units.list enum, return
            return { totalValue, value, unit };
        }
        const { preferredUnit, preferredUnitText } = this.getPreferredUnitData({
            sourceUnit,
            preferredUnits,
            conversionScale,
            pluralizeUnit,
            value,
            showUnitAbbr
        });

        const displayValue = DataUtils.convertTo({
            value,
            sourceUnit,
            convertToUnit: preferredUnit,
            roundOff,
            padLeft,
            decimals
        });
        const displayTotalValue = DataUtils.convertTo({
            value: totalValue,
            sourceUnit,
            convertToUnit: preferredUnit,
            roundOff,
            padLeft,
            decimals
        });
        const valueString = !DataUtils.isEmpty(displayValue)
            ? `${displayValue} ${preferredUnitText}`
            : '';
        return {
            totalValue: displayTotalValue,
            value: displayValue,
            unit: preferredUnitText,
            valueString
        };
    };

    basicConvert = (
        options: ConvertValueOptionsType & { targetUnit: string }
    ) => {
        const { value, sourceUnit, targetUnit } = options;
        return DataUtils.convertTo({
            value,
            sourceUnit,
            convertToUnit: targetUnit
        });
    };

    getPreferredUnitData = options => {
        const {
            pluralizeUnit,
            sourceUnit,
            unit,
            preferredUnits,
            value,
            conversionScale,
            showUnitAbbr
        } = options;
        const preferredUnit = this.getPreferredUnit({
            preferredUnits,
            conversionScale,
            sourceUnit
        });

        const preferredUnitText = this.getConvertedUnitDisplayText({
            conversionScale,
            sourceUnit,
            unit,
            preferredUnits,
            pluralizeUnit,
            value,
            showUnitAbbr
        });

        return {
            preferredUnit,
            preferredUnitText
        };
    };

    getPreferredUnit = options => {
        const {
            preferredUnits,
            sourceUnit,
            conversionScale = Units.conversionScale.Factor
        } = options;
        const unitType = sourceUnit.measure;

        const preferredSystem = this.getPreferredSystem({ unitType });

        if (preferredUnits) {
            // forced preferred system for both metric and imperial
            if (preferredUnits.length === 1) {
                return preferredUnits[0];
            }

            const unit = preferredUnits.find(preferredUnit => {
                return (
                    preferredUnit.system ===
                    UnitSystemToConvertSystemMap[preferredSystem]
                );
            });
            if (unit) {
                return unit;
            }
        }
        return Units.conversionDefinition[preferredSystem][unitType][
            conversionScale
        ];
    };

    getPreferredSystem = ({ unitType }: { unitType: string }) => {
        const preferredMapping =
            this.dimensionsMapping &&
            this.dimensionsMapping.find(dimensionMap => {
                return (
                    dimensionMap.dimension ===
                    unitMeasureToDimensionsMapping[unitType]
                );
            });
        return preferredMapping
            ? preferredMapping.system
            : UnitSystemsEnum.Metric;
    };

    getConvertedUnitDisplayText = options => {
        // unit display text after converting
        // by default converts to Factor
        const {
            sourceUnit,
            unit,
            preferredUnits,
            conversionScale = Units.conversionScale.Factor,
            pluralizeUnit = true,
            value = 0,
            showUnitAbbr = true
        } = options;
        const preferredUnit = this.getPreferredUnit({
            preferredUnits,
            conversionScale,
            sourceUnit
        });
        let displayUnit =
            sourceUnit && sourceUnit.abbr ? sourceUnit.abbr : unit;
        if (showUnitAbbr && preferredUnit) {
            displayUnit =
                preferredUnit.abbr === 'h' ? 'hr' : preferredUnit.abbr;
        } else if (preferredUnit) {
            displayUnit = preferredUnit.singular;
        }
        if (pluralizeUnit) {
            displayUnit = StringUtils.pluralize(
                displayUnit,
                value,
                null,
                false
            );
        }
        if (displayUnit === 'mis') {
            //Because mis is horrible
            displayUnit = 'mi';
        }
        if (displayUnit === 'ins') {
            //Because ins is horrible
            displayUnit = 'in';
        }
        return displayUnit && displayUnit.toLowerCase();
    };

    convertToSeconds = (options: ConvertToOptionsType) => {
        const {
            value,
            conversionScale = Units.conversionScale.Base, // convert from Base (minutes)
            convertToUnit = Units.list.time.Second
        } = options;
        return this.convertFromPreferredUnit({
            value,
            conversionScale,
            convertToUnit
        });
    };

    convertToMeters = (options: ConvertToOptionsType) => {
        const {
            value,
            conversionScale = Units.conversionScale.Factor, // convert from Factor (miles/kms)
            convertToUnit = Units.list.length.Meter // used to find preferred unit (miles/kms)
        } = options;
        return this.convertFromPreferredUnit({
            value,
            conversionScale,
            convertToUnit
        });
    };

    convertToKilogram = (options: ConvertToOptionsType) => {
        const {
            value,
            conversionScale = Units.conversionScale.Factor, // convert from Factor (pounds/kgs)
            convertToUnit = Units.list.mass.Kilogram // used to find preferred unit (pounds/kgs)
        } = options;
        return this.convertFromPreferredUnit({
            value,
            conversionScale,
            convertToUnit
        });
    };

    convertFromPreferredUnit = options => {
        const {
            value,
            totalValue,
            convertToUnit,
            conversionScale,
            roundOff,
            padLeft,
            decimals,
            pluralizeUnit,
            showUnitAbbr
        } = options;
        if (!convertToUnit || !convertToUnit.abbr) {
            return { totalValue, value, unit: convertToUnit };
        }
        const { preferredUnit, preferredUnitText } = this.getPreferredUnitData({
            sourceUnit: convertToUnit,
            conversionScale,
            pluralizeUnit,
            value,
            showUnitAbbr
        });
        const displayValue = DataUtils.convertTo({
            value,
            sourceUnit: preferredUnit,
            convertToUnit,
            roundOff,
            padLeft,
            decimals
        });
        const displayTotalValue = DataUtils.convertTo({
            value: totalValue,
            sourceUnit: preferredUnit,
            convertToUnit,
            roundOff,
            padLeft,
            decimals
        });
        const valueString = !DataUtils.isEmpty(displayValue)
            ? `${displayValue} ${preferredUnitText}`
            : '';
        return {
            totalValue: displayTotalValue,
            value: displayValue,
            unit: preferredUnitText,
            valueString
        };
    };
}
